import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useInfiniteQuery } from "react-query";
import { axiosInstance } from "api/axios";
import Search from "assets/icons/search.gif";
import {
  DataGridPremium,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GridColDef,
  GridColumnVisibilityModel,
  GridInitialState,
  GridRenderCellParams,
  GridRowGroupingModel,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import moment from "moment";
import PackagesContext from "contexts/PackagesContext";
import KeysContext from "contexts/KeysContext";
import { IPackage } from "dto/IPackage";
import NameCell from "./Cells/NameCell";
import StatusCell from "./Cells/StatusCell";
import tagsService from "api/tagsService";
import statusService from "api/statusService";
import TagCell from "./Cells/TagCell";
import { IAttachment } from "dto/IAttachment";
import IconsCell from "./Cells/IconsCell";
import MenuCell from "./Cells/MenuCell";
import PreviewModal from "components/PreviewModal";
import { plGridLocales } from "utils/muiGridLocales";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import ReceivedByCell from "./Cells/ReceivedByCell";
import PassedToCell from "./Cells/PassedToCell";
import ShowPackageModal from "components/ShowPackageModal";
import { nullHandlingSortComparator } from "utils/nullHandlingSortComparator";
import CustomToolbar from "components/CaseDossier/Toolbar";
import Spinner from "components/Spinner";
import { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import { ListType } from "dto/ListType";
import { StatusType } from "dto/IStatus";
import { TagType } from "dto/ITag";
import { IGridFilter } from "dto/IGridFilter";
import { ItemKind } from "dto/IKindItems";
import { cleanCourtName } from "utils/cleanCourtName";
import { useTranslation } from "react-i18next";
import useGridRowSelection from "hooks/useGridRowSelection";

const CourtPortalDeliveranceRegistry: FC<Props> = ({
  id,
  searchText,
  kind,
  setSubtitle,
}) => {
  const { t } = useTranslation();
  const { selectedRowIds, rowsRef, handleRowClick } = useGridRowSelection();

  const { data: tagsData } = tagsService.useGetTags();
  const { data: statusesData, isLoading: isLoadingStatuses } =
    statusService.useGetStatuses();

  const statuses = useMemo(() => statusesData?.data ?? [], [statusesData]);
  const tags = useMemo(() => tagsData?.data ?? [], [tagsData]);

  const { packages, setPackages } = useContext(PackagesContext);
  const { keys, setKeys } = useContext(KeysContext);

  const [incoming, setIncoming] = useState<boolean>(true);
  const [key] = useState(0);
  const gridSettingsStorageKey = () =>
    incoming
      ? `grid_deliveranceRegister_${id ?? "none"}_incoming`
      : `grid_deliveranceRegister_${id ?? "none"}_outgoing`;
  const [showTypeModal, setShowTypeModal] = useState<boolean>(false);

  const [preview, setPreview] = useState<IPackage>(null);
  const [, setPreviewItemParent] = useState<IPackage | null>(null);
  const [previewItem, setPreviewItem] = useState<IAttachment | null>(null);
  const [showPreview, setShowPreview] = useState<boolean>(false);

  const [filterStatusId, setFilterStatusId] = useState<number>(null);
  const [filterTagId, setFilterTagId] = useState<string>(null);
  const [filterUserId, setFilterUserId] = useState<string>(null);
  const [editFilter, setEditFilter] = useState<IGridFilter>(null);

  const CustomNoRowsOverlay = () => {
    return (
      <div className={styles.empty}>
        <img src={Search} />
        <div>{t("emptyStates.noResults")}</div>
      </div>
    );
  };

  const CustomToolbar2 = () => {

    return (
      <CustomToolbar
        listName="messagesRegister"
        editFilter={editFilter}
        setEditFilter={setEditFilter}
        listType={ListType.Incoming}
        statusId={filterStatusId}
        setStatusId={setFilterStatusId}
        tagId={filterTagId}
        setTagId={setFilterTagId}
        statusType={StatusType.INCOMING}
        tagType={TagType.Incoming}
        userId={filterUserId}
        setUserId={setFilterUserId}
        dateFilterLabel={t("columns.date")}
        rightChildren={
          <>
            {kind !== ItemKind.CourtPortalDeliverance && (
              <div className={styles.type}>
                <div
                  onClick={() => setShowTypeModal(!showTypeModal)}
                  className={styles.typeText}
                >
                  {incoming ? t("packages.incoming") : t("packages.outgoing")}
                  <FontAwesomeIcon
                    icon={showTypeModal ? faChevronUp : faChevronDown}
                  />
                </div>
                {showTypeModal && (
                  <div className={styles.dropdown}>
                    <div
                      onClick={() => {
                        saveSnapshotInternal();
                        setIncoming(true);
                        setShowTypeModal(false);
                      }}
                      className={styles.box}
                    >
                      {t("packages.incoming")}
                    </div>
                    <div
                      onClick={() => {
                        saveSnapshotInternal();
                        setIncoming(false);
                        setShowTypeModal(false);
                      }}
                      className={styles.box}
                    >
                      {t("packages.outgoing")}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        }
      ></CustomToolbar>
    );
  };

  const toolbar = () => <CustomToolbar2></CustomToolbar2>;

  useEffect(() => {
    setSubtitle(incoming ? t("packages.incoming") : t("packages.outgoing"));
  }, []);

  const {
    data,
    fetchNextPage,
    isLoading,
    hasNextPage,
    refetch,
  } = useInfiniteQuery(
    ["packages", kind, incoming, filterStatusId, filterTagId, filterUserId],
    async ({ pageParam = "" }) => {
      const query = new URLSearchParams(window.location.search);
      if (searchText.length) {
        query.append("Text", searchText);
      }

      query.append("IsIncoming", incoming ? "true" : "false");
      query.append("IncludeRemoved", "true");
      query.append("FromRegistry", "true");
      query.append("Group", "true");

      if (kind) {
        query.append("Kind", kind.toString());
      }
      query.append("PageToken", pageParam);
      query.append("SortProp", "Date");
      query.append("SortOrder", "Descending");

      if (filterStatusId) {
        query.append("StatusId", filterStatusId.toString());
      }

      if (filterTagId) {
        query.append("TagId", filterTagId.toString());
      }

      const res = await axiosInstance.get(`/list?${query}`);
      return res.data;
    },
    {
      enabled: false,
      staleTime: 0,
      cacheTime: 0,
      getNextPageParam: (lastPage) => {
        if (!lastPage.LastPage) {
          return lastPage.PageToken;
        }
        return undefined;
      },
    },
  );

  const [isInitialized, setIsInitialized] = useState(false);

  const compileData = async () => {
    if (!data) return;

    const packs = await Promise.all(
      data?.pages
        .map((page) => {
          return page.Items;
        })
        .flat(),

      // .sort((left: IPackage, right: IPackage) => {
      //   //@ts-ignore
      //   return right.Unread - left.Unread;
      // })
    );

    const newMap = packs.map((p: IPackage) => {
      const oldPack = packages.find((a) => a.Id === p.Id);

      if (oldPack?.isDelete) {
        p.isDelete = true;
      }

      return p;
    });

    const objectKeys = await Promise.all(
      data?.pages
        .map((page) => {
          return page.ObjectKeys;
        })
        .flat(),
    );

    setPackages(newMap.flat());
    // @ts-ignore
    setKeys((prev) => [...prev, ...objectKeys]);
    setIsInitialized(true);
  };

  const onDownload = async (
    pack: IPackage,
    attachment: IAttachment,
    key?: any,
  ) => {
    // setDownloadFileName(attachment.FileName);
    // create(pack.Id, attachment, pack.Kind, true);

    return;
  };

  const onPreview = async (
    pack: IPackage,
    attachment: IAttachment,
    key?: any,
  ) => {
    setPreviewItem(attachment);
    setPreviewItemParent(pack);
    setShowPreview(true);
    return;
  };

  useEffect(() => {
    compileData();
  }, [data]);

  useEffect(() => {
    setPackages([]);
    setIsInitialized(false);

    setTimeout(() => {
      refetch();
    }, 500);
  }, [kind, incoming, searchText, filterStatusId, filterTagId, filterUserId]);

  const getStatus = (item: IPackage) => {
    if (!item.StatusId) {
      if (item.ReceiveDate) return t("statuses.received");

      return null;
    }

    const s = statuses.find((x) => x.Id == item.StatusId);

    if (s) {
      return s.Name;
    }

    return null;
  };

  const getFirstTag = (item: IPackage) => {
    if (!item.TagIds || item.TagIds.length === 0) return null;

    const t = tags.find((x) => x.Id == item.TagIds[0]);

    if (t) {
      return t.Name;
    }

    return null;
  };

  const apiRef = useGridApiRef();

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  const [groupingModel, setGroupingModel] = useState<GridRowGroupingModel>([]);

  const saveSnapshotInternal = () => {
    const settingsKey = gridSettingsStorageKey();
    console.log("Saving grid settings", settingsKey);
    if (apiRef?.current?.exportState && localStorage) {
      const currentState = apiRef.current.exportState();
      localStorage.setItem(settingsKey, JSON.stringify(currentState));
    }
  };

  const saveSnapshot = useCallback(() => {
    saveSnapshotInternal();
  }, [apiRef]);

  const [initialState, setInitialState] = useState<GridInitialState>(null);

  useLayoutEffect(() => {
    console.log("Layout effect");
    const stateFromLocalStorage = localStorage?.getItem(
      gridSettingsStorageKey(),
    );
    const data: GridInitialStatePremium = stateFromLocalStorage
      ? JSON.parse(stateFromLocalStorage)
      : ({
          columns: {
            columnVisibilityModel: {
              AvailableDate: false,
              S1: false,
            },
          },
        } as GridInitialStatePremium);
    data.pinnedColumns = { right: ["Menu"] };
    setInitialState(data);
    setColumnVisibilityModel(
      data?.columns?.columnVisibilityModel ?? {
        S1: false,
        AvailableDate: false,
      },
    );
    setGroupingModel(data.rowGrouping?.model ?? []);

    // handle refresh and navigating away/refreshing
    window.addEventListener("beforeunload", saveSnapshot);

    return () => {
      // in case of an SPA remove the event-listener
      window.removeEventListener("beforeunload", saveSnapshot);
      saveSnapshot();
    };
  }, [saveSnapshot]);

  const hiddenFields = [
    "Label",
    "IsFolder",
    GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
    "Icons",
    "Menu",
  ];

  const getTogglableColumns = (columns: GridColDef[]) => {
    return columns
      .filter((column) => !hiddenFields.includes(column.field))
      .map((column) => column.field);
  };

  const columns: GridColDef[] = [
    {
      field: "Label",
      minWidth: 180,
      flex: 1,
      headerName: incoming ? t("packages.senderHeader") : t("forms.recipient"),
      cellClassName: `${styles.nameCell}`,
      valueGetter: (value, row) => row?.S1,
      sortComparator: nullHandlingSortComparator,
      groupingValueGetter: (value, row) =>
        incoming ? row.Sender : row.Receivers.map((r) => r.Name).join(", "),
      renderCell: (params: GridRenderCellParams<IPackage, string>) =>
        params.rowNode.type !== "group" ? (
          kind != ItemKind.CourtPortalDeliverance ? (
            <NameCell element={params.row}></NameCell>
          ) : (
            <div
              dangerouslySetInnerHTML={{
                __html: cleanCourtName(params.row.Sender),
              }}
            ></div>
          )
        ) : (
          <div>{params.value}</div>
        ),
    },
    {
      field: "Signature",
      minWidth: 140,
      headerName: t("forms.signature"),
      cellClassName: `${styles.nameCell}`,
      valueGetter: (value, row) => row?.Case?.Signature,
      sortComparator: nullHandlingSortComparator,
      renderCell: (c) => {
        return (
          <span
            dangerouslySetInnerHTML={{ __html: c.row?.Case?.Signature }}
          ></span>
        );
      },
    },
    {
      field: "Client",
      minWidth: 140,
      headerName: t("cases.client"),
      cellClassName: `${styles.nameCell}`,
      valueGetter: (value, row) => row?.Case?.Client,
      renderCell: (c) => {
        return (
          <span
            dangerouslySetInnerHTML={{ __html: c.row?.Case?.Client }}
          ></span>
        );
      },
      sortComparator: nullHandlingSortComparator,
    },
    {
      field: "S1",
      minWidth: 140,
      headerName: t("columns.documentSign"),
      cellClassName: `${styles.nameCell}`,
      valueGetter: (value, row) => row?.S1,
      sortComparator: nullHandlingSortComparator,
      renderCell: (c) => {
        return <span dangerouslySetInnerHTML={{ __html: c.row?.S1 }}></span>;
      },
    },
    {
      field: "Status",
      minWidth: 140,
      headerName: t("common.status"),
      headerAlign: "center",
      align: "center",
      cellClassName: `${styles.statusCell}`,
      valueGetter: (value, row) => getStatus(row),
      groupingValueGetter: (value, row) => getStatus(row) ?? t("common.noStatus"),
      sortComparator: nullHandlingSortComparator,
      renderCell: (params: GridRenderCellParams<IPackage, string>) =>
        params.rowNode.type !== "group" ? (
          <StatusCell element={params.row} statuses={statuses}></StatusCell>
        ) : (
          <div>{params.value}</div>
        ),
    },
    {
      field: "TagIds",
      minWidth: 140,
      headerName: t("forms.label"),
      headerAlign: "center",
      align: "center",
      cellClassName: `${styles.nameCell}`,
      valueGetter: (value, row) => getFirstTag(row),
      groupingValueGetter: (value, row) => getFirstTag(row) ?? t("common.noLabel"),
      sortComparator: nullHandlingSortComparator,
      renderCell: (params: GridRenderCellParams<IPackage, string>) =>
        params.rowNode.type !== "group" ? (
          <TagCell element={params.row} tags={tags}></TagCell>
        ) : (
          <div>{params.value}</div>
        ),
    },
    {
      field: "SendDate",
      width: 140,
      headerName: t("columns.sentDate"),
      groupable: false,
      valueFormatter: (v) => {
        if (v) {
          return moment(v).format("yy.MM.DD");
        }
        return null;
      },
    },
    {
      field: "AvailableDate",
      width: 140,
      headerName: t("columns.arrivedDate"),
      groupable: false,
      valueFormatter: (v) => {
        if (v) {
          return moment(v).format("yy.MM.DD");
        }
        return null;
      },
    },
    {
      field: "ReceiveDate",
      width: 140,
      headerName: t("columns.receivedDate"),
      groupable: false,
      valueFormatter: (v) => {
        if (v) {
          return moment(v).format("yy.MM.DD");
        }
        return null;
      },
    },
    {
      field: "ReceiverdBy",
      width: 140,
      headerName: t("columns.receivedBy"),
      sortComparator: nullHandlingSortComparator,
      valueGetter: (value, row) => {
        row.ReceivedBy && row.ReceivedBy.length > 0
          ? row.ReceivedBy
          : row.ReceiveDate && row.Receivers && row.Receivers.length > 0
          ? row.Receivers[0].Name
          : "";
      },
      groupingValueGetter: (value, row) =>
        row.ReceivedBy && row.ReceivedBy.length > 0
          ? row.ReceivedBy
          : row.ReceiveDate && row.Receivers && row.Receivers.length > 0
          ? row.Receivers[0].Name
          : t("common.none"),
      renderCell: (params: GridRenderCellParams<IPackage, string>) =>
        params.rowNode.type !== "group" ? (
          <ReceivedByCell element={params.row}></ReceivedByCell>
        ) : (
          <div>{params.value}</div>
        ),
    },
    {
      field: "PassedTo",
      width: 140,
      headerName: t("columns.passedTo"),
      sortComparator: nullHandlingSortComparator,
      groupingValueGetter: (value, row) => row.Pass?.To ?? t("registry.noTransfer"),
      renderCell: (params: GridRenderCellParams<IPackage, string>) => (
        <PassedToCell element={params.row}></PassedToCell>
      ),
    },
    {
      field: "Icons",
      width: 140,
      headerName: t("courtPortalDeliveranceRegistry.icons"),
      disableReorder: true,
      sortable: false,
      resizable: false,
      hideable: false,
      groupable: false,
      disableColumnMenu: true,
      renderHeader: () => <div></div>,
      renderCell: (params: GridRenderCellParams<IPackage, string>) => (
        <IconsCell
          element={params.row}
          onDownload={onDownload}
          onPreview={(pack, attachment) => {
            setPreviewItemParent(pack);
            setPreviewItem(attachment);
            setShowPreview(true);
          }}
          keys={keys}
        ></IconsCell>
      ),
    },
    {
      field: "Menu",
      width: 40,
      headerName: t("courtPortalDeliveranceRegistry.menu"),
      disableReorder: true,
      sortable: false,
      resizable: false,
      hideable: false,
      groupable: false,
      disableColumnMenu: true,
      renderHeader: () => <div></div>,
      renderCell: (params: GridRenderCellParams<IPackage, string>) => (
        <MenuCell
          element={params.row}
          onDownload={onDownload}
          onPreview={onPreview}
          keys={keys}
        ></MenuCell>
      ),
    },
  ];

  const columnsMemo = useMemo(
    () =>
      kind != ItemKind.CourtPortalDeliverance
        ? columns.filter((x) => x.field !== "S1")
        : columns,
    [],
  );

  if (!initialState || !statusesData?.data || !tagsData?.data) {
    return <Spinner />;
  }

  const rows = packages.map((c) => {
    //@ts-ignore
    c.id = c.Id;
    return c;
  });
  rowsRef.current = rows;

  return (
    <div className={styles.wrapper}>
      <div className={styles.list}>
        {/* {packages.length === 0 && !isLoading && isInitialized && (
        
        )} */}

        <DataGridPremium
          key={key}
          apiRef={apiRef}
          sx={{
            border: 0,
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              {
                outline: "none !important",
              },
          }}
          initialState={{ ...initialState, pinnedColumns: { right: ["Menu"] } }}
          localeText={plGridLocales}
          slots={{ toolbar: toolbar, noRowsOverlay: CustomNoRowsOverlay }}
          showToolbar
          slotProps={{
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
            columnsManagement: {
              getTogglableColumns,
            },
          }}
          loading={!isInitialized || isLoading || isLoadingStatuses}
          columnHeaderHeight={30}
          onRowsScrollEnd={() => {
            if (hasNextPage) {
              fetchNextPage();
            }
          }}
          disableRowSelectionOnClick
          rowSelectionModel={selectedRowIds}
          onRowClick={handleRowClick}
          onRowDoubleClick={(r) => {
            const p = r.row as IPackage;
            setPreview(p);
          }}
          columns={columnsMemo}
          hideFooter={true}
          disablePivoting={true}
          rows={rows}
          disableColumnFilter={true}
          disableAggregation={true}
          onColumnVisibilityModelChange={(m, d) => {
            console.log("ON Column visibility model changed", m);
            setColumnVisibilityModel(m);

            setTimeout(() => saveSnapshotInternal(), 1000);
          }}
          onColumnOrderChange={(p) => {
            setTimeout(() => saveSnapshotInternal(), 1000);
          }}
          onRowGroupingModelChange={(m, d) => {
            const newVisibility = { ...columnVisibilityModel };

            for (let i = 0; i < groupingModel.length; i++) {
              newVisibility[groupingModel[i]] = true;
            }

            for (let i = 0; i < m.length; i++) {
              newVisibility[m[i]] = false;
            }
            setColumnVisibilityModel(newVisibility);
            setGroupingModel(m);

            setTimeout(() => saveSnapshotInternal(), 1000);
          }}
          columnVisibilityModel={columnVisibilityModel}
        />
      </div>

      {preview && (
        <ShowPackageModal
          previewPackage={preview as IPackage}
          toggle={() => setPreview(null)}
        />
      )}

      {showPreview && previewItem && (
        <div className={styles.modalBg}>
          <PreviewModal
            itemId={previewItem.Id}
            itemKind={previewItem.Kind}
            fileUri={previewItem.Uri}
            encryptionKeyId={previewItem.EncryptionKeyId}
            fileName={previewItem.FileName}
            toggle={() => setShowPreview(false)}
            refetch={() => refetch()}
          ></PreviewModal>
        </div>
      )}
    </div>
  );
};

export default CourtPortalDeliveranceRegistry;
